// Quake 3 / Warfork nickname colour codes (`^1`..`^7`, `^0`).
//
// `^` followed by a digit switches colour; `^^` is a literal `^`. Used for
// menu rows and the floating in-game player name tags.

const Q3_COLORS: Record<string, string> = {
  "0": "#000000", // black
  "1": "#ff0000", // red
  "2": "#00ff00", // green
  "3": "#ffff00", // yellow
  "4": "#0000ff", // blue
  "5": "#00ffff", // cyan
  "6": "#ff00ff", // magenta
  "7": "#ffffff", // white
  "8": "#ff8000", // orange (Q3 extension)
};

export interface ColorSpan {
  text: string;
  color: string;
}

/** Split a Quake3-coloured string into `{ text, color }` runs (default white). */
export function parseQ3Colors(s: string): ColorSpan[] {
  const out: ColorSpan[] = [];
  let color = "#ffffff";
  let buf = "";
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (c === "^" && i + 1 < s.length) {
      const next = s[i + 1];
      if (next === "^") {
        buf += "^";
        i++;
        continue;
      }
      const col = Q3_COLORS[next];
      if (col !== undefined) {
        if (buf) out.push({ text: buf, color });
        buf = "";
        color = col;
        i++;
        continue;
      }
    }
    buf += c;
  }
  if (buf) out.push({ text: buf, color });
  return out;
}

/** The visible text with colour codes removed. */
export function stripQ3Colors(s: string): string {
  return parseQ3Colors(s)
    .map((p) => p.text)
    .join("");
}

const escapeHtml = (t: string) =>
  t.replace(/[&<>"]/g, (ch) =>
    ch === "&" ? "&amp;" : ch === "<" ? "&lt;" : ch === ">" ? "&gt;" : "&quot;",
  );

/** Coloured nickname as safe HTML (each run in a `<span style="color:…">`). */
export function q3Html(s: string): string {
  return parseQ3Colors(s)
    .map((p) => `<span style="color:${p.color}">${escapeHtml(p.text)}</span>`)
    .join("");
}

/** Does the string use colour codes? (so callers can keep their default colour) */
export function hasQ3Colors(s: string): boolean {
  return /\^[0-9^]/.test(s);
}

/** Append Quake-coloured text to an element as spans (no HTML injection). */
export function appendQ3Text(el: HTMLElement, s: string): void {
  for (const p of parseQ3Colors(s)) {
    const span = document.createElement("span");
    span.style.color = p.color;
    span.textContent = p.text;
    el.append(span);
  }
}
